import { Injectable } from '@angular/core';
import { State, Action, StateContext, Store } from '@ngxs/store';
import {
  AttendanceCAU,
  AttendanceCAUFile,
  AttendanceCauFileAddForm,
  AttendanceCauRequest,
  PresenceDay,
  ProposalAttendanceCau,
  WorkingAttendanceCau,
} from '../../models/attendance-cau.model';
import { AttendanceService } from '../../services/attendance.service';
import { AttendanceActions } from './attendance.actions';
import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { MassiveActions } from 'src/app/modules/massive-actions/store/massive/massive.actions';
import { AttendanceStatusItem } from '../../models/attendance-status-item.model';
import { ValidateAttendance } from '../../models/validate-attendance.model';
import { patch, updateItem } from '@ngxs/store/operators';
import {
  UpdateAttendanceCAU,
  UpdateAttendanceCAUMassive,
  UpdateAttendanceStudentCAU,
} from '../../models/update-attendance-cau.model';
import { AlertTypeEnum } from 'src/app/shared/store/alert/alert-type-enum';
import { AttendanceFilter } from '../../models/attendance-filter.model';
import {
  StudentAttendance,
  WorkingStudentAttendance,
} from '../../models/student-attendance.model';
import { AttendanceDay } from '../../models/attendance-day.model';
import { MonthDay } from '../../models/month-day.model';
import {
  UpdateAttendanceStudentRequest,
  UpdateStudentAttendance,
  UpdateStudentAttendanceMassive,
} from '../../models/update-student-attendance.model';
import { AttendanceContractTypeEnum } from '../../enums/attendance-contract-type.enum';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';
import { AttendanceCAUFilter } from '../../models/attendance-cau-filters.model';

export class AttendanceStateModel {
  public attendanceCAU: AttendanceCAU;
  public attendanceCAUFiles: AttendanceCAUFile[];
  public workingAttendanceCaus: WorkingAttendanceCau[];
  public daysInMonth: MonthDay[];
  public isLoading: boolean;
  attendanceCauFileForm?: {
    model: AttendanceCauRequest;
  };
  validateAttendanceForm?: {
    model: ValidateAttendance;
  };
  attendanceCauFileAddForm: {
    model: AttendanceCauFileAddForm;
  };
  studentAttendances?: StudentAttendance[];
  isListSmaller: boolean;
  isMatchingData: boolean;
  filters: {
    model: AttendanceFilter;
  };
  filtersCAU: {
    model: AttendanceCAUFilter;
  };
  workingStudentAttendances?: WorkingStudentAttendance[];
  showNoBillable: boolean;
  reloadData: boolean;
  isBilled: boolean;
  billingDate: Date;
  billingUser: string;
  isValidated: boolean;
  validateDate: Date;
  validateUser: string;
  error: string;
}

const defaults = {
  attendanceCAU: null,
  attendanceCAUFiles: null,
  workingAttendanceCaus: [],
  daysInMonth: [],
  attendanceCauFileForm: {
    model: null,
  },
  validateAttendanceForm: {
    model: null,
  },
  attendanceCauFileAddForm: {
    model: null,
  },
  filters: {
    model: null,
  },
  filtersCAU: {
    model: null,
  },
  attendanceCauRequest: new AttendanceCauRequest(),
  isLoading: false,
  showProposal: false,
  isListSmaller: true,
  isMatchingData: false,
  studentAttendances: [],
  workingStudentAttendances: [],
  showNoBillable: false,
  reloadData: false,
  isBilled: false,
  billingDate: null,
  billingUser: '',
  isValidated: false,
  validateDate: null,
  validateUser: '',
  error: '',
};

@State<AttendanceStateModel>({
  name: 'attendance',
  defaults,
})
@Injectable()
export class AttendanceState {
  constructor(
    private attendanceService: AttendanceService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store
  ) {}

  @Action(AttendanceActions.GetStudentAttendances)
  getStudentAttendances(
    ctx: StateContext<AttendanceStateModel>,
    {}: AttendanceActions.GetStudentAttendances
  ) {
    var state = ctx.getState();
    ctx.patchState({ isLoading: true });
    return this.attendanceService
      .getStudentAttendances(state.filters.model)
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new AttendanceActions.GetStudentAttendancesSuccess(response)
          );
        },
        error: (error) => ctx.dispatch(new AttendanceActions.Error(error, '')),
      });
  }

  @Action(AttendanceActions.GetStudentAttendancesSuccess)
  getStudentAttendancesSuccess(
    ctx: StateContext<AttendanceStateModel>,
    { studentsAttendances }: AttendanceActions.GetStudentAttendancesSuccess
  ) {
    let state = ctx.getState();
    let year = state.filters.model.year;
    let month = state.filters.model.month;
    let exerciseId = state.filters.model.exerciseId;
    ctx.setState(
      patch({
        isLoading: false,
        reloadData: true,
        isBilled: studentsAttendances?.isBilled,
        billingDate: studentsAttendances?.billingDate,
        billingUser: studentsAttendances?.billingUser,
        isValidated: studentsAttendances?.isValidated,
        validateDate: studentsAttendances?.validateDate,
        validateUser: studentsAttendances?.validateUser,
        studentAttendances: studentsAttendances?.studentAttendances,
        workingStudentAttendances: this.getWorkingStudentAttendance(
          studentsAttendances?.studentAttendances,
          year,
          month,
          exerciseId
        ),
        isMatchingData: studentsAttendances?.studentAttendances.length > 0,
      })
    );
  }

  @Action(AttendanceActions.GetAttendanceCau)
  getAttendanceCau(
    ctx: StateContext<AttendanceStateModel>,
    { attendanceCAUFileId, exerciseId }: AttendanceActions.GetAttendanceCau
  ) {
    ctx.patchState({ isLoading: true });
    return this.attendanceService
      .getAttendanceCau(attendanceCAUFileId)
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new AttendanceActions.GetAttendanceCauSuccess(response, exerciseId)
          );
        },
        error: (error) => ctx.dispatch(new AttendanceActions.Error(error, '')),
      });
  }

  @Action(AttendanceActions.GetAttendanceCauSuccess)
  getAttendanceCauSuccess(
    ctx: StateContext<AttendanceStateModel>,
    {
      attendancesCAUResponse,
      exerciseId,
    }: AttendanceActions.GetAttendanceCauSuccess
  ) {
    ctx.setState(
      patch({
        isLoading: false,
        reloadData: true,
        attendanceCAU: attendancesCAUResponse,
        workingAttendanceCaus: this.getWorkingAttendanceCaus(
          attendancesCAUResponse,
          attendancesCAUResponse.year,
          attendancesCAUResponse.month,
          exerciseId
        ).sort((a, b) => a.studentName.localeCompare(b.studentName)),
      })
    );
  }

  @Action(AttendanceActions.GetAttendancesCau)
getAttendancesCau(
  ctx: StateContext<AttendanceStateModel>,
  { exerciseId, year, month, user }: AttendanceActions.GetAttendancesCau
) {
  ctx.patchState({ isLoading: true });
  
  return this.attendanceService
    .getAttendanceFilesCau({ exerciseId, year, month}, user)
    .subscribe({
      next: (response) => {
        ctx.dispatch(new AttendanceActions.GetAttendancesCauSuccess(response));
      },
      error: (error) => ctx.dispatch(new AttendanceActions.Error(error, '')),
    });
}


  @Action(AttendanceActions.GetAttendancesCauSuccess)
  getAttendancesCauSuccess(
    ctx: StateContext<AttendanceStateModel>,
    { attendanceCAUFiles }: AttendanceActions.GetAttendancesCauSuccess
  ) {
    ctx.patchState({
      attendanceCAUFiles: attendanceCAUFiles,
      isLoading: false,
    });
  }

  @Action(AttendanceActions.UpdateAttendanceCauMassive)
  updateAttendanceCauMassive(
    ctx: StateContext<AttendanceStateModel>,
    {
      attendanceCAUFileId,
      user,
    }: AttendanceActions.UpdateAttendanceCauMassive
  ) {
    var state = ctx.getState();
    ctx.patchState({ isLoading: true });

    let updateAttendanceCAUMassive = new UpdateAttendanceCAUMassive();
    updateAttendanceCAUMassive.attendanceCAUFileId = attendanceCAUFileId;
    updateAttendanceCAUMassive.updatePresenceStudentCAUs =
      state.workingAttendanceCaus
        .filter((y) => y.isEdited)
        .map((x) => {
          return new UpdateAttendanceStudentCAU(
            x.studentId,
            x.studentName,
            x.attendanceDays.map((y) => {
              return new PresenceDay(y.day, y.attendanceService);
            })
          );
        });
    return this.attendanceService
      .updateAttendanceCAUMassive(updateAttendanceCAUMassive, user)
      .subscribe({
        next: (response) =>
          ctx.dispatch(
            new AttendanceActions.UpdateAttendanceCauMassiveSuccess(response)
          ),
        error: (error) => ctx.dispatch(new AttendanceActions.Error(error, '')),
      });
  }

  @Action(AttendanceActions.UpdateAttendanceCauMassiveSuccess)
  updateAttendanceCauMassiveSuccess(
    ctx: StateContext<AttendanceStateModel>,
    { massiveResponse }: AttendanceActions.UpdateAttendanceCauMassiveSuccess
  ) {
    let state = ctx.getState();
    this.store.dispatch(new MassiveActions.SetMassiveResponse(massiveResponse));
    if (massiveResponse.itemError && massiveResponse.itemError.length > 0) {
      return;
    }

    ctx.setState(
      patch({
        isLoading: false,
        workingAttendanceCaus: state.workingAttendanceCaus.map((x) => {
          if (x.isEdited)
            return {
              ...x,
              isEdited: false,
              isProcessing: true,
            };
          return x;
        }),
      })
    );
  }

  @Action(AttendanceActions.UpdateStudentAttendanceMassive)
  updateStudentAttendanceMassive(
    ctx: StateContext<AttendanceStateModel>,
    {
      user,
    }: AttendanceActions.UpdateStudentAttendanceMassive
  ) {
    var state = ctx.getState();
    ctx.patchState({ isLoading: true });
    let updateStudentAttendanceMassive = new UpdateStudentAttendanceMassive();
    updateStudentAttendanceMassive.serviceId = state.filters.model.serviceId;
    updateStudentAttendanceMassive.month = state.filters.model.month;
    updateStudentAttendanceMassive.year = state.filters.model.year;
    updateStudentAttendanceMassive.schoolId = state.filters.model.schoolId;
    updateStudentAttendanceMassive.exerciseId = state.filters.model.exerciseId;
    updateStudentAttendanceMassive.updatePresenceStudentCAUs =
      state.workingStudentAttendances
        .filter((y) => y.isEdited)
        .map((x) => {
          return new UpdateAttendanceStudentRequest(
            x.studentId,
            x.studentName,
            x.attendanceDays.map((y) => {
              return new PresenceDay(y.day, y.attendanceService);
            })
          );
        });

    return this.attendanceService
      .updateStudentAttendanceMassive(updateStudentAttendanceMassive, user)
      .subscribe({
        next: (response) =>
          ctx.dispatch(
            new AttendanceActions.UpdateStudentAttendanceMassiveSuccess(
              response
            )
          ),
        error: (error) => ctx.dispatch(new AttendanceActions.Error(error, '')),
      });
  }

  @Action(AttendanceActions.UpdateStudentAttendanceMassiveSuccess)
  updateStudentAttendanceMassiveSuccess(
    ctx: StateContext<AttendanceStateModel>,
    { massiveResponse }: AttendanceActions.UpdateStudentAttendanceMassiveSuccess
  ) {
    let state = ctx.getState();
    this.store.dispatch(new MassiveActions.SetMassiveResponse(massiveResponse));
    if (massiveResponse.itemError && massiveResponse.itemError.length > 0) {
      return;
    }

    ctx.setState(
      patch({
        isLoading: false,
        workingStudentAttendances: state.workingStudentAttendances.map((x) => {
          if (x.isEdited)
            return {
              ...x,
              isEdited: false,
              isProcessing: true,
            };
          return x;
        }),
      })
    );
  }

  @Action(AttendanceActions.UpdateAttendanceCAU)
  updateAttendanceCAU(
    ctx: StateContext<AttendanceStateModel>,
    { studentId, index, user }: AttendanceActions.UpdateAttendanceCAU
  ) {
    var state = ctx.getState();
    let updateAttendanceCAU = new UpdateAttendanceCAU();
    updateAttendanceCAU.serviceId = state.attendanceCAU.serviceId;
    updateAttendanceCAU.month = state.attendanceCAU.month;
    updateAttendanceCAU.year = state.attendanceCAU.year;
    updateAttendanceCAU.studentId = studentId;
    updateAttendanceCAU.presenceDays = state.workingAttendanceCaus
      .find((y) => y.index === index)
      .attendanceDays.map((y) => {
        return new PresenceDay(y.day, y.attendanceService);
      });

    return this.attendanceService
      .updateAttendanceCAU(updateAttendanceCAU, user)
      .subscribe({
        next: () =>
          ctx.dispatch(new AttendanceActions.UpdateAttendanceCAUSuccess(index)),
        error: (error) => ctx.dispatch(new AttendanceActions.Error(error, '')),
      });
  }

  @Action(AttendanceActions.UpdateAttendanceCAUSuccess)
  updateAttendanceCAUSuccess(
    ctx: StateContext<AttendanceStateModel>,
    { index }: AttendanceActions.UpdateAttendanceCAUSuccess
  ) {
    let state = ctx.getState();
    let workingAttendanceCau = state.workingAttendanceCaus.find(
      (x) => x.index === index
    );

    ctx.setState(
      patch({
        isLoading: false,
        attendanceCAU: patch({
          proposalAttendanceCaus: updateItem<ProposalAttendanceCau>(
            (x) => x.studentId === workingAttendanceCau.studentId,
            patch({
              proposalDays: workingAttendanceCau.attendanceDays.map((x) => {
                return new AttendanceDay(
                  x.day,
                  x.attendanceService,
                  false,
                  false
                );
              }),
            })
          ),
        }),
        workingAttendanceCaus: updateItem<WorkingAttendanceCau>(
          (x) => x.index === index,
          patch({
            isEdited: false,
            isProcessing: true,
          })
        ),
      })
    );
  }

  @Action(AttendanceActions.UpdateStudentAttendance)
  updateStudentAttendance(
    ctx: StateContext<AttendanceStateModel>,
    { studentId, index, user }: AttendanceActions.UpdateStudentAttendance
  ) {
    var state = ctx.getState();
    let updateStudentAttendance = new UpdateStudentAttendance();
    updateStudentAttendance.serviceId = state.filters.model.serviceId;
    updateStudentAttendance.month = state.filters.model.month;
    updateStudentAttendance.year = state.filters.model.year;
    updateStudentAttendance.studentId = studentId;
    updateStudentAttendance.presenceDays = state.workingStudentAttendances
      .find((y) => y.index === index)
      .attendanceDays.map((y) => {
        return new PresenceDay(y.day, y.attendanceService);
      });

    return this.attendanceService
      .updateStudentAttendance(updateStudentAttendance, user)
      .subscribe({
        next: () =>
          ctx.dispatch(
            new AttendanceActions.UpdateStudentAttendanceSuccess(index)
          ),
        error: (error) => ctx.dispatch(new AttendanceActions.Error(error, '')),
      });
  }

  @Action(AttendanceActions.UpdateStudentAttendanceSuccess)
  updateStudentAttendanceSuccess(
    ctx: StateContext<AttendanceStateModel>,
    { index }: AttendanceActions.UpdateStudentAttendanceSuccess
  ) {
    let state = ctx.getState();
    let workingStudentAttendance = state.workingStudentAttendances.find(
      (x) => x.index === index
    );

    ctx.setState(
      patch({
        isLoading: false,
        studentAttendances: updateItem<StudentAttendance>(
          (x) => x.studentId === workingStudentAttendance.studentId,
          patch({
            attendanceDays: workingStudentAttendance.attendanceDays.map((x) => {
              return new AttendanceDay(
                x.day,
                x.attendanceService,
                false,
                false
              );
            }),
          })
        ),
        workingStudentAttendances: updateItem<WorkingStudentAttendance>(
          (x) => x.index === index,
          patch({
            isEdited: false,
            isProcessing: true,
          })
        ),
      })
    );
  }

  @Action(AttendanceActions.ValidateAttendanceSchool)
  validateAttendanceSchool(
    ctx: StateContext<AttendanceStateModel>,
    { user }: AttendanceActions.ValidateAttendanceSchool
  ) {
    var state = ctx.getState();

    ctx.patchState({ isLoading: true });
    return this.attendanceService
      .validateAttendanceSchool(state.validateAttendanceForm.model, user)
      .subscribe({
        next: () => {
          ctx.dispatch(new AttendanceActions.ValidateAttendanceSchoolSuccess());
        },
        error: (error) => ctx.dispatch(new AttendanceActions.Error(error, '')),
      });
  }

  @Action(AttendanceActions.ValidateAttendanceSchoolSuccess)
  validateAttendanceSchoolSuccess(
    ctx: StateContext<AttendanceStateModel>,
    {}: AttendanceActions.ValidateAttendanceSchoolSuccess
  ) {
    ctx.setState(patch({ isLoading: false }));
  }

  @Action(AttendanceActions.SetDaysInMonth) setDaysInMonth(
    ctx: StateContext<AttendanceStateModel>,
    { year, month }: AttendanceActions.SetDaysInMonth
  ) {
    var state = ctx.getState();
    let daysInMonth = this.getDaysInMonth(year, month);
    ctx.patchState({
      ...state,
      isLoading: false,
      daysInMonth,
    });
  }

  @Action(AttendanceActions.SetFileExercise)
  setFileExercise(
    ctx: StateContext<AttendanceStateModel>,
    { exerciseId }: AttendanceActions.SetFileExercise
  ) {
    ctx.setState(
      patch({
        attendanceCauFileForm: patch({
          model: patch({
            exerciseId: exerciseId,
          }),
        }),
      })
    );
  }

  @Action(AttendanceActions.SetOriginalAttendanceDays)
  setOriginalAttendanceDays(
    ctx: StateContext<AttendanceStateModel>,
    { studentId }: AttendanceActions.SetOriginalAttendanceDays
  ) {    
    var state = ctx.getState();
    let year = state.attendanceCAU.year;
    let month = state.attendanceCAU.month;
    let proposalAttendanceCau: any = state.attendanceCAU.proposalAttendanceCaus.find(
      (x) => x.studentId === studentId
    );

    ctx.setState(
      patch({
        workingAttendanceCaus: updateItem<WorkingAttendanceCau>(
          (attendanceCAU) => attendanceCAU.studentId === studentId,
          patch({
            attendanceDays: proposalAttendanceCau.attendanceDays.map((x) => {
              return this.getStatusItem(
                proposalAttendanceCau.index,
                x,
                year,
                month,
                proposalAttendanceCau.attendanceContractTypeEnum
              );
            }),
            isEdited: false,
          })
        ),
        reloadData: true,
      })
    );
  }

  @Action(AttendanceActions.SetProposalAttendanceDays)
  setProposalAttendanceDays(
    ctx: StateContext<AttendanceStateModel>,
    { studentId }: AttendanceActions.SetProposalAttendanceDays
  ) {
    var state = ctx.getState();
    let year = state.attendanceCAU.year;
    let month = state.attendanceCAU.month;
    let proposalAttendanceCau : any = state.attendanceCAU.proposalAttendanceCaus.find(
      (x) => x.studentId === studentId
    );

    ctx.setState(
      patch({
        workingAttendanceCaus: updateItem<WorkingAttendanceCau>(
          (attendanceCAU) => attendanceCAU.studentId === studentId,
          patch({
            attendanceDays: proposalAttendanceCau.proposalDays.map((x) => {
              return this.getStatusItem(
                proposalAttendanceCau.index,
                x,
                year,
                month,
                proposalAttendanceCau.attendanceContractTypeEnum
              );
            }),
            isEdited: true,
          })
        ),
        reloadData: true,
      })
    );
  }

  @Action(AttendanceActions.SetIsListSmaller)
  setIsListSmaller(
    ctx: StateContext<AttendanceStateModel>,
    { isListSmaller }: AttendanceActions.SetIsListSmaller
  ) {
    ctx.patchState({
      isListSmaller: isListSmaller,
    });
  }

  @Action(AttendanceActions.SetReloadData)
  setReloadData(
    ctx: StateContext<AttendanceStateModel>,
    { value }: AttendanceActions.SetReloadData
  ) {
    ctx.patchState({
      reloadData: value,
    });
  }

  @Action(AttendanceActions.SetIsLoading)
  setIsLoading(
    ctx: StateContext<AttendanceStateModel>,
    { value }: AttendanceActions.SetIsLoading
  ) {
    ctx.patchState({
      isLoading: value,
    });
  }

  @Action(AttendanceActions.SetShowNoBillable)
  setShowNoBillable(
    ctx: StateContext<AttendanceStateModel>,
    { showNoBillable }: AttendanceActions.SetShowNoBillable
  ) {
    ctx.patchState({
      showNoBillable,
    });
  }

  @Action(AttendanceActions.EditTableCell)
  editTableCell(
    ctx: StateContext<AttendanceStateModel>,
    { index, day, newValue }: AttendanceActions.EditTableCell
  ) {
    ctx.setState(
      patch({
        workingStudentAttendances: updateItem<WorkingStudentAttendance>(
          (workingStudentAttendance) =>
            workingStudentAttendance.index === index,
          patch({
            attendanceDays: updateItem<AttendanceStatusItem>(
              (attendanceStatusItem) => attendanceStatusItem.day === day,
              patch({
                attendanceService: newValue,
              })
            ),
          })
        ),
        isLoading: false,
      })
    );

    var state = ctx.getState();
    var workingStudentAttendance = state.workingStudentAttendances.find(
      (x) => x.index === index
    );
    var studentAttendance = state.studentAttendances.find(
      (x) => x.studentId === workingStudentAttendance.studentId
    );
    ctx.setState(
      patch({
        workingStudentAttendances: updateItem<WorkingStudentAttendance>(
          (workingStudentAttendance) =>
            workingStudentAttendance.index === index,
          patch({
            isEdited: this.hasDifference(
              workingStudentAttendance,
              studentAttendance
            ),
          })
        ),
      })
    );
  }

  @Action(AttendanceActions.EditTableCellCau)
  editTableCellCau(
    ctx: StateContext<AttendanceStateModel>,
    { index, day, newValue }: AttendanceActions.EditTableCellCau
  ) {
    ctx.setState(
      patch({
        workingAttendanceCaus: updateItem<WorkingAttendanceCau>(
          (workingAttendanceCau) => workingAttendanceCau.index === index,
          patch({
            attendanceDays: updateItem<AttendanceStatusItem>(
              (attendanceStatusItem) => attendanceStatusItem.day === day,
              patch({
                attendanceService: newValue,
              })
            ),
          })
        ),
        isLoading: false,
      })
    );

    var state = ctx.getState();
    var workingAttendanceCau = state.workingAttendanceCaus.find(
      (x) => x.index === index
    );
    var proposalAttendanceCau = state.attendanceCAU.proposalAttendanceCaus.find(
      (x) => x.studentId === workingAttendanceCau.studentId
    );
    ctx.setState(
      patch({
        workingAttendanceCaus: updateItem<WorkingAttendanceCau>(
          (workingStudentAttendance) =>
            workingStudentAttendance.index === index,
          patch({
            isEdited: this.hasDifference(
              workingAttendanceCau,
              proposalAttendanceCau
            ),
          })
        ),
      })
    );
  }

  @Action(AttendanceActions.LoadAttendanceCauFile)
  loadAttendanceCauFile(
    ctx: StateContext<AttendanceStateModel>,
    { user, attendanceCauFileRequest }: AttendanceActions.LoadAttendanceCauFile
  ) {
    this.alertService
      .confirm(
        this.getTitle(),
        this.localizationService.translate(
          'attendance.attencanceCauFileLoadConfirm'
        ),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService.translate(
          'attendance.attencanceCauFileLoadConfirm'
        )
      )
      .then((result) => {
        if (result.value) {
          ctx.patchState({
            isLoading: true,
          });
          return this.attendanceService
            .loadAttendanceCauFile(user, attendanceCauFileRequest)
            .subscribe({
              next: () => {
                ctx.dispatch(
                  new AttendanceActions.LoadAttendanceCauFileSuccess()
                );
              },
              error: (error) =>
                ctx.dispatch(new AttendanceActions.Error(error, '')),
            });
        } else {
          return null;
        }
      });
  }

  @Action(AttendanceActions.LoadAttendanceCauFileSuccess)
  loadAttendanceCauFileSuccess(
    ctx: StateContext<AttendanceStateModel>,
    {}: AttendanceActions.LoadAttendanceCauFileSuccess
  ) {
    ctx.setState(
      patch({
        isLoading: false,
      })
    );
  }

  @Action(AttendanceActions.Error)
  onErrorOcurred(
    ctx: StateContext<AttendanceStateModel>,
    { error, details }: AttendanceActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoading: false,
    });
    MappingUtilsService.showError(
      this.getTitle(),
      error,
      this.alertService,
      this.localizationService
    );
  }

  @Action(AttendanceActions.SetDefaultState)
  setDefaultState(
    ctx: StateContext<AttendanceStateModel>,
    {}: AttendanceActions.SetDefaultState
  ) {
    ctx.setState(defaults);
  }

  @Action(AttendanceActions.SetValidateAttendanceFormDefaultState)
  setValidateAttendanceFormDefaultState(
    ctx: StateContext<AttendanceStateModel>,
    {}: AttendanceActions.SetValidateAttendanceFormDefaultState
  ) {
    ctx.setState(
      patch({
        validateAttendanceForm: patch({
          model: defaults.validateAttendanceForm.model,
        }),
      })
    );
  }

  @Action(AttendanceActions.SetAttendanceCauFileAddDefault)
  setFamilyAddDefaultState(
    ctx: StateContext<AttendanceStateModel>,
    {}: AttendanceActions.SetAttendanceCauFileAddDefault
  ) {
    ctx.setState(
      patch({
        attendanceCauFileAddForm: patch({
          model: defaults.attendanceCauFileAddForm.model,
        }),
      })
    );
  }

  @Action(AttendanceActions.SetAttendanceCAUDefaultState)
  setAttendanceCAUDefaultState(
    ctx: StateContext<AttendanceStateModel>,
    {}: AttendanceActions.SetAttendanceCAUDefaultState
  ) {
    ctx.setState(
      patch({
        attendanceCAU: defaults.attendanceCAU,
        workingAttendanceCaus: defaults.workingAttendanceCaus,
      })
    );
  }

  private getWorkingStudentAttendance(
    studentAttendances: StudentAttendance[],
    year: number,
    month: number,
    exerciseId: number
  ): WorkingStudentAttendance[] {
    return studentAttendances.map((row: StudentAttendance, index: number) => {
      let newWorkingAttendanceCau = new WorkingStudentAttendance();
      newWorkingAttendanceCau.index = index;
      newWorkingAttendanceCau.studentId = row.studentId;
      newWorkingAttendanceCau.exerciseId = exerciseId;
      newWorkingAttendanceCau.studentCau = row.studentCau;
      newWorkingAttendanceCau.studentName = row.studentName;
      newWorkingAttendanceCau.studentLastName = row.studentLastName;
      newWorkingAttendanceCau.schoolarCourseId = row.schoolarCourseId;
      newWorkingAttendanceCau.schoolarCourse = row.schoolarCourse;
      newWorkingAttendanceCau.schoolarshipId = row.schoolarshipId;
      newWorkingAttendanceCau.schoolarshipName = row.schoolarshipName;
      newWorkingAttendanceCau.studentContractType = row.studentContractType;
      newWorkingAttendanceCau.familyId = row.familyId;
      newWorkingAttendanceCau.familyAccount = row.familyAccount;
      newWorkingAttendanceCau.customerNumber = row.customerNumber;
      newWorkingAttendanceCau.attendanceCount = row.attendanceCount;
      newWorkingAttendanceCau.hasDiscount1 = row.hasDiscount1;
      newWorkingAttendanceCau.hasDiscount2 = row.hasDiscount2;
      newWorkingAttendanceCau.hasDiscount3 = row.hasDiscount3;
      newWorkingAttendanceCau.isEdited = false;
      newWorkingAttendanceCau.isProcessing = false;
      newWorkingAttendanceCau.attendanceDays = row.attendanceDays.map((x) => {
        return this.getStatusItem(
          index,
          x,
          year,
          month,
          AttendanceContractTypeEnum.Usual
        );
      });
      return newWorkingAttendanceCau;
    });
  }

  private getWorkingAttendanceCaus(
    attendancesCAUResponse: AttendanceCAU,
    year: number,
    month: number,
    exerciseId: number
  ) {
    var response = attendancesCAUResponse.proposalAttendanceCaus.map(
      (row, index: number) => {
        return this.getWorkingAttendanceCau(
          row,
          year,
          month,
          exerciseId,
          index
        );
      }
    );
    return response;
  }

  private getWorkingAttendanceCau(
    proposalAttendanceCau: ProposalAttendanceCau,
    year: number,
    month: number,
    exerciseId: number,
    index: number
  ) {
    let workingAttendanceCau =
      proposalAttendanceCau as unknown as WorkingAttendanceCau;
    workingAttendanceCau.isEdited = false;
    workingAttendanceCau.exerciseId = exerciseId;
    workingAttendanceCau.index = index;
    workingAttendanceCau.hasDiscrepancies = workingAttendanceCau.hasDiscrepancies;
    workingAttendanceCau.showProposalButton = workingAttendanceCau.showProposalButton;
    workingAttendanceCau.attendanceDays =
      proposalAttendanceCau.attendanceDays.map((x) => {
        return this.getStatusItem(
          index,
          x,
          year,
          month,
          proposalAttendanceCau.attendanceContractTypeEnum
        );
      });
    return workingAttendanceCau;
  }

  private getTitle() {
    return this.localizationService.translate('attendance.title');
  }

  private getDaysInMonth(year, month) {
    var date = new Date(year, month - 1, 1);
    var days: MonthDay[] = [];

    while (date.getMonth() === month - 1) {
      days.push({
        value: date.getDate(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        dayOfWeek: date.getDay(),
        hasService: false
      });
      date.setDate(date.getDate() + 1);
    }
    return days;
  }

  private getStatusItem(
    index: number,
    attendanceDay: AttendanceDay,
    year: number,
    month: number,
    attendanceContractTypeEnum: AttendanceContractTypeEnum
  ) {
    //let currentDate = new Date(year, month - 1, attendanceDay.day);
    let isHoliday = attendanceDay.isHoliday; // || currentDate.getDay() === 0 || currentDate.getDay() === 6;
    return new AttendanceStatusItem(
      attendanceDay.day,
      attendanceDay.attendanceService,
      index,
      isHoliday,
      attendanceContractTypeEnum
    );
  }

  private hasDifference(
    workingStudentAttendance: WorkingStudentAttendance | WorkingAttendanceCau,
    studentAttendance: StudentAttendance | ProposalAttendanceCau
  ): boolean {
    var result = studentAttendance.attendanceDays.filter((attendanceDay) => {
      return workingStudentAttendance.attendanceDays.some(
        (x) =>
          x.day === attendanceDay.day &&
          x.attendanceService !== attendanceDay.attendanceService
      );
    });
    return result.length > 0;
  }
}
